import { ClinicalEventEntry, type EntryDescriptor, type EntryField } from './ClinicalEventEntry';
import type { BirthWorkspaceProps } from './LaborFlowsheet';
import type { ProposedEntry } from './PrenatalEntry';
const choice = (key: string, label: string, values: string[]): EntryField => ({
  key,
  label,
  options: values.map((value) => ({ value, label: value.replaceAll('_', ' ') })),
});
const text = (key: string, label: string): EntryField => ({ key, label });
const time = (key: string, label: string): EntryField => ({ key, label, kind: 'datetime' });
export const transportDescriptor: EntryDescriptor = {
  eventType: 'TRANSPORT',
  schemaVersion: 3,
  label: 'Transport / transfer of care',
  fields: [
    choice('transport_type', 'Transport type', ['INTRAPARTUM', 'POSTPARTUM_MATERNAL', 'NEWBORN']),
    time('decision_at', 'Decision time'),
    text('deciding_provider_id', 'Deciding provider'),
    text('contact_phone', 'Provider contact phone'),
    choice('urgency', 'Urgency', ['EMERGENT', 'URGENT', 'NON_URGENT']),
    { key: 'reasons', label: 'Reasons for transfer (one per line)', kind: 'string-array' },
    { key: 'reason_other', label: 'Other reason / clinical context', kind: 'textarea' },
    choice('mode', 'Transport mode', ['EMS', 'PRIVATE_VEHICLE', 'OTHER']),
    text('transporting_from', 'Transporting from'),
    text('receiving_facility.name', 'Receiving facility'),
    text('receiving_facility.phone', 'Receiving facility phone'),
    text('receiving_facility.contact_name', 'Receiving clinician / contact'),
    text('receiving_facility.contact_role', 'Receiving contact role'),
    time('facility_notified_at', 'Facility notified'),
    { key: 'condition_at_transport', label: 'Condition at transport', kind: 'textarea' },
    { key: 'ems.called', label: 'EMS called', kind: 'boolean' },
    time('ems.called_at', 'EMS called at'),
    time('ems.arrived_at', 'EMS arrived at'),
    time('ems.departed_at', 'EMS departed at'),
    time('arrived_facility_at', 'Arrived at receiving facility'),
    { key: 'accompanying', label: 'Accompanying people (one per line)', kind: 'string-array' },
    text('disposition_of_mother', 'Maternal disposition for newborn transfer'),
    ...['situation', 'background', 'assessment', 'recommendation'].map((key) => ({
      key: `handoff_sbar.${key}`,
      label: `SBAR ${key}`,
      kind: 'textarea' as const,
    })),
    { key: 'outcome_followup', label: 'Outcome / follow-up details', kind: 'textarea' },
  ],
};
export function transportIssues(payload: Record<string, unknown>): string[] {
  const errors: string[] = [];
  const ems = payload.ems as Record<string, unknown> | undefined;
  const values = [ems?.called_at, ems?.arrived_at, ems?.departed_at, payload.arrived_facility_at].filter(
    (v): v is string => typeof v === 'string',
  );
  for (let i = 1; i < values.length; i++)
    if (Date.parse(values[i]) < Date.parse(values[i - 1]))
      errors.push('EMS and arrival timestamps must follow their documented chronological order.');
  if (
    typeof payload.decision_at === 'string' &&
    typeof payload.arrived_facility_at === 'string' &&
    Date.parse(payload.arrived_facility_at) < Date.parse(payload.decision_at)
  )
    errors.push('Facility arrival cannot precede the decision time.');
  if (ems?.called === false && ems.called_at) errors.push('EMS is marked not called but has a call time.');
  return errors;
}
export function TransportEntry(props: BirthWorkspaceProps) {
  async function save(entries: ProposedEntry[], at: string) {
    for (const entry of entries) {
      const subject = props.episode.subjects.find((s) => s.id === entry.subject_id);
      if ((entry.payload.transport_type === 'NEWBORN') !== (subject?.subject_type === 'NEWBORN'))
        throw Error('Choose the matching maternal or newborn subject for this transport type.');
      const errors = transportIssues(entry.payload);
      if (errors.length) throw Error(errors.join(' '));
    }
    await props.onSave(entries, at);
  }
  return (
    <details data-encounter-draft className="rounded-lg border p-4">
      <summary className="min-h-11 cursor-pointer font-semibold">Document transport / transfer of care</summary>
      <ClinicalEventEntry
        descriptors={[transportDescriptor]}
        subjects={props.episode.subjects.filter((s) => s.active !== false)}
        events={props.events}
        onSave={save}
        timeZone={props.timeZone}
        disabled={props.disabled || !props.encounterId}
      />
    </details>
  );
}
